/**
 * DocumentDrafter — Generador de documents i protocols integrat a l'assistent IA.
 * Permet generar esborranys editables de protocols, accions correctives i textos de documentació.
 */
import React, { useState } from "react";
import hosys from "@/api/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Loader2, Sparkles, Copy, Check, RefreshCw, ChevronRight, ArrowLeft,
  AlertTriangle, SprayCan, ShieldCheck, FileText, ClipboardCheck, Thermometer,
} from "lucide-react";

// ─── Catàleg de plantilles ────────────────────────────────────────────────────
const CATEGORIES = [
  {
    id: "corrective",
    label: "Accions correctives",
    icon: AlertTriangle,
    color: "text-red-500",
    bg: "bg-red-50",
    templates: [
      {
        id: "corrective_action",
        label: "Acció correctiva",
        description: "Text per corregir una incidència detectada",
        fields: [{ key: "incident", label: "Descriu la incidència", type: "textarea" }],
        prompt: (f) => `Genera una acció correctiva professional i concisa per a la incidència de seguretat alimentària següent: "${f.incident}". 
Inclou: 1) Mesures immediates a prendre, 2) Causa probable, 3) Mesures preventives per evitar recurrència. 
Format: text clar, directe, usable en un registre APPCC oficial. En català.`,
      },
      {
        id: "preventive",
        label: "Mesures preventives",
        description: "Mesures per evitar que es repeteixi una incidència",
        fields: [{ key: "incident", label: "Incidència o risc a prevenir", type: "textarea" }],
        prompt: (f) => `Genera un llistat de mesures preventives per evitar la incidència o risc següent: "${f.incident}".
Inclou mesures de caràcter operacional, formatiu i de supervisió. Format: punts clars i accionables. En català.`,
      },
      {
        id: "observation",
        label: "Observació d'incidència",
        description: "Redacta les observacions d'un registre",
        fields: [
          { key: "incident", label: "Situació observada", type: "textarea" },
          { key: "severity", label: "Gravetat (baixa/mitjana/alta/crítica)", type: "input" },
        ],
        prompt: (f) => `Redacta les observacions formals per a un registre d'incidència de gravetat "${f.severity}" amb la situació: "${f.incident}".
El text ha de ser objectiu, concís i adequat per a un document oficial. En català.`,
      },
    ],
  },
  {
    id: "protocols",
    label: "Protocols bàsics",
    icon: ShieldCheck,
    color: "text-blue-500",
    bg: "bg-blue-50",
    templates: [
      {
        id: "cleaning_protocol",
        label: "Protocol de neteja",
        description: "Procediment de neteja per a una zona o equip",
        fields: [
          { key: "zone", label: "Zona o equip (ex: cuina, cambra frigorífica...)", type: "input" },
          { key: "frequency", label: "Freqüència (diàri, setmanal...)", type: "input" },
        ],
        prompt: (f) => `Genera un protocol de neteja i desinfecció per a: "${f.zone}", amb freqüència "${f.frequency}".
Inclou: productes recomanats, concentracions, temps d'acció, equips de protecció i procediment pas a pas.
Basat en la normativa APPCC i Reglament CE 852/2004. En català.`,
      },
      {
        id: "food_handling",
        label: "Protocol manipulació aliments",
        description: "Procediment de manipulació per a un producte o procés",
        fields: [{ key: "product", label: "Producte o procés (ex: carn crua, descongelació...)", type: "input" }],
        prompt: (f) => `Genera un protocol de manipulació d'aliments per a: "${f.product}".
Inclou: condicions de temperatura, higiene personal, prevenció de contaminació creuada i emmagatzematge correcte.
Basat en la normativa APPCC. En català.`,
      },
      {
        id: "internal_procedure",
        label: "Procediment intern",
        description: "Procediment operatiu estàndard (SOP)",
        fields: [
          { key: "process", label: "Procés o activitat a documentar", type: "input" },
          { key: "responsible", label: "Responsable (rol)", type: "input" },
        ],
        prompt: (f) => `Genera un procediment intern (SOP) per a l'activitat: "${f.process}", responsable: "${f.responsible}".
Inclou: objectiu, abast, materials necessaris, passos detallats i registre/verificació.
Format professional per a manual de qualitat intern. En català.`,
      },
      {
        id: "work_instruction",
        label: "Instrucció de treball",
        description: "Guia ràpida pas a pas per a una tasca",
        fields: [{ key: "task", label: "Tasca o operació", type: "input" }],
        prompt: (f) => `Genera una instrucció de treball clara i visual (pas a pas) per a: "${f.task}".
Ha de ser comprensible per a personal sense formació específica. Curta, directa, amb numeració de passos. En català.`,
      },
    ],
  },
  {
    id: "documentation",
    label: "Textos de documentació",
    icon: FileText,
    color: "text-purple-500",
    bg: "bg-purple-50",
    templates: [
      {
        id: "recipe_description",
        label: "Descripció fitxa tècnica",
        description: "Text descriptiu per a una fitxa tècnica de plat",
        fields: [
          { key: "dish", label: "Nom del plat", type: "input" },
          { key: "ingredients", label: "Ingredients principals", type: "input" },
        ],
        prompt: (f) => `Genera una descripció professional per a la fitxa tècnica del plat: "${f.dish}" amb els ingredients principals: "${f.ingredients}".
Inclou: descripció sensorial (aspecte, sabor, aroma, textura), elaboració breu i presentació. Estil formal per a fitxa tècnica. En català.`,
      },
      {
        id: "verification_obs",
        label: "Observació de verificació",
        description: "Text d'observació per a un registre de verificació APPCC",
        fields: [
          { key: "type", label: "Tipus de verificació (ex: control temperatura, neteja...)", type: "input" },
          { key: "result", label: "Resultat obtingut", type: "input" },
        ],
        prompt: (f) => `Genera el text d'observació per a un registre de verificació APPCC de "${f.type}" amb el resultat: "${f.result}".
Redacta de forma objectiva, formal i adequada per a un document de traçabilitat. En català.`,
      },
      {
        id: "incident_report",
        label: "Informe d'incidència",
        description: "Redacta un informe complet d'una incidència",
        fields: [
          { key: "what", label: "Què va passar", type: "textarea" },
          { key: "when", label: "Quan i on", type: "input" },
          { key: "action", label: "Acció presa", type: "input" },
        ],
        prompt: (f) => `Redacta un informe formal d'incidència alimentària amb les dades:
- Descripció: "${f.what}"
- Data/lloc: "${f.when}"  
- Acció presa: "${f.action}"
Inclou: resum executiu, descripció detallada, causa probable i mesures adoptades. Format professional per a registre oficial. En català.`,
      },
      {
        id: "storage_notes",
        label: "Notes de conservació",
        description: "Instruccions de conservació per a un producte",
        fields: [
          { key: "product", label: "Producte o plat", type: "input" },
          { key: "temp", label: "Temperatura de conservació", type: "input" },
        ],
        prompt: (f) => `Genera les notes de conservació per al producte: "${f.product}" a temperatura: "${f.temp}".
Inclou: condicions de conservació, vida útil, indicadors d'alteració i instruccions d'ús. Format per a etiqueta o fitxa tècnica. En català.`,
      },
    ],
  },
];

// ─── Component principal ──────────────────────────────────────────────────────
export default function DocumentDrafter() {
  const [step, setStep] = useState("categories"); // categories | templates | form | result
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [fieldValues, setFieldValues] = useState({});
  const [generatedText, setGeneratedText] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const reset = () => {
    setStep("categories");
    setSelectedCategory(null);
    setSelectedTemplate(null);
    setFieldValues({});
    setGeneratedText("");
    setCopied(false);
  };

  const selectCategory = (cat) => {
    setSelectedCategory(cat);
    setStep("templates");
  };

  const selectTemplate = (tpl) => {
    setSelectedTemplate(tpl);
    setFieldValues({});
    setStep("form");
  };

  const generate = async () => {
    setLoading(true);
    try {
      const prompt = selectedTemplate.prompt(fieldValues);
      const SYSTEM = `Ets un expert en gestió de restaurants i seguretat alimentària (normativa APPCC, Reglament CE 852/2004).
Genera textos professionals, clars i pràctics en català.
Retorna ÚNICAMENT el text sol·licitat, sense introducció, comentaris ni explicació addicional.
El text ha de ser directament usable en un document oficial o registre intern.`;
      const response = await hosys.integrations.Core.InvokeLLM({ prompt: `${SYSTEM}\n\n${prompt}` });
      setGeneratedText(typeof response === "string" ? response : JSON.stringify(response));
      setStep("result");
    } catch {
      setGeneratedText("No s'ha pogut generar el text. Torna-ho a intentar.");
      setStep("result");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const allFieldsFilled = selectedTemplate?.fields?.every(f => fieldValues[f.key]?.trim());

  // ── Views ──────────────────────────────────────────────────────────────────

  if (step === "categories") {
    return (
      <div className="flex flex-col gap-3 py-2">
        <p className="text-xs text-gray-500 px-1 font-medium">Selecciona el tipus de document a generar:</p>
        {CATEGORIES.map(cat => {
          const Icon = cat.icon;
          return (
            <button
              key={cat.id}
              onClick={() => selectCategory(cat)}
              className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:border-[#7B2D3F]/20 hover:bg-[#f9f0f2]/40 transition-all text-left group"
            >
              <div className={`w-9 h-9 rounded-lg ${cat.bg} flex items-center justify-center flex-shrink-0`}>
                <Icon className={`w-4.5 h-4.5 ${cat.color}`} style={{ width: "18px", height: "18px" }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-800">{cat.label}</p>
                <p className="text-xs text-gray-400">{cat.templates.length} plantilles disponibles</p>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-[#7B2D3F] transition-colors flex-shrink-0" />
            </button>
          );
        })}
      </div>
    );
  }

  if (step === "templates") {
    const Icon = selectedCategory.icon;
    return (
      <div className="flex flex-col gap-2 py-1">
        <button onClick={() => setStep("categories")} className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 mb-1 self-start">
          <ArrowLeft className="w-3.5 h-3.5" /> Tornar
        </button>
        <div className={`flex items-center gap-2 p-2.5 rounded-lg ${selectedCategory.bg} mb-1`}>
          <Icon className={`w-4 h-4 ${selectedCategory.color}`} />
          <p className="text-xs font-semibold text-gray-700">{selectedCategory.label}</p>
        </div>
        {selectedCategory.templates.map(tpl => (
          <button
            key={tpl.id}
            onClick={() => selectTemplate(tpl)}
            className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:border-[#7B2D3F]/20 hover:bg-[#f9f0f2]/40 transition-all text-left group"
          >
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-800">{tpl.label}</p>
              <p className="text-xs text-gray-400 mt-0.5">{tpl.description}</p>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-[#7B2D3F] flex-shrink-0" />
          </button>
        ))}
      </div>
    );
  }

  if (step === "form") {
    return (
      <div className="flex flex-col gap-3 py-1">
        <button onClick={() => setStep("templates")} className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 self-start">
          <ArrowLeft className="w-3.5 h-3.5" /> Tornar
        </button>
        <div className="bg-[#f9f0f2]/60 rounded-xl p-3 border border-[#7B2D3F]/10">
          <p className="text-sm font-semibold text-gray-800">{selectedTemplate.label}</p>
          <p className="text-xs text-gray-500 mt-0.5">{selectedTemplate.description}</p>
        </div>
        <div className="space-y-3">
          {selectedTemplate.fields.map(field => (
            <div key={field.key} className="space-y-1">
              <label className="text-xs font-medium text-gray-600">{field.label}</label>
              {field.type === "textarea" ? (
                <textarea
                  value={fieldValues[field.key] || ""}
                  onChange={e => setFieldValues(prev => ({ ...prev, [field.key]: e.target.value }))}
                  rows={3}
                  className="w-full text-sm bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#7B2D3F]/20 focus:border-[#7B2D3F]/40 resize-none placeholder:text-gray-400"
                  placeholder="Descriu la situació..."
                />
              ) : (
                <input
                  value={fieldValues[field.key] || ""}
                  onChange={e => setFieldValues(prev => ({ ...prev, [field.key]: e.target.value }))}
                  className="w-full text-sm bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#7B2D3F]/20 focus:border-[#7B2D3F]/40 placeholder:text-gray-400"
                  placeholder="Escriu aquí..."
                />
              )}
            </div>
          ))}
        </div>
        <button
          onClick={generate}
          disabled={loading || !allFieldsFilled}
          className="w-full flex items-center justify-center gap-2 py-3 bg-gradient-brand text-white rounded-xl font-medium text-sm transition-all hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed mt-1"
        >
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Generant...</> : <><Sparkles className="w-4 h-4" /> Generar esborrany</>}
        </button>
      </div>
    );
  }

  if (step === "result") {
    return (
      <div className="flex flex-col gap-3 py-1">
        <div className="flex items-center justify-between">
          <button onClick={() => setStep("form")} className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600">
            <ArrowLeft className="w-3.5 h-3.5" /> Tornar
          </button>
          <div className="flex gap-1">
            <button onClick={generate} disabled={loading}
              className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors" title="Regenerar">
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            </button>
            <button onClick={handleCopy}
              className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors" title="Copiar">
              {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>

        <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Esborrany generat — editable</p>
        <textarea
          value={generatedText}
          onChange={e => setGeneratedText(e.target.value)}
          rows={10}
          className="w-full text-sm bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#7B2D3F]/20 focus:border-[#7B2D3F]/40 resize-none leading-relaxed"
        />
        <p className="text-[11px] text-gray-400">✏️ Edita el text lliurement abans de copiar-lo o guardar-lo</p>

        <div className="flex gap-2">
          <button onClick={handleCopy}
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 border border-gray-200 hover:bg-gray-50 rounded-xl text-sm font-medium text-gray-600 transition-all">
            {copied ? <><Check className="w-4 h-4 text-emerald-500" /> Copiat!</> : <><Copy className="w-4 h-4" /> Copiar text</>}
          </button>
          <button onClick={reset}
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-gradient-brand text-white rounded-xl text-sm font-medium transition-all hover:opacity-90">
            <Sparkles className="w-4 h-4" /> Nou document
          </button>
        </div>
      </div>
    );
  }

  return null;
}